import { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Skeleton } from '@mui/material'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'


const ImagePlaceholder = () => (
  <Skeleton
    sx={{
      margin: '0px 16px 16px 0px',
      borderRadius: '4px',
      objectFit: 'cover',
    }}
    variant="rectangular"
    width={90}
    height={90}/>
)


const LightboxGallery = ({
  attachments = [],
  attachmentsCount = 0,
  isLoading = false,
}) => {
  const [loaded, setLoaded] = useState([])
  const [blobUrls, setBlobUrls] = useState([])
  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)

  // Reset loaded array whenever attachments change
  useEffect(() => {
    setLoaded(new Array(attachments.length).fill(false))
    setBlobUrls(new Array(attachments.length).fill(null))
  }, [attachments])

  // Fetch Toyota protected images → Blob URLs
  useEffect(() => {
    if (!attachments.length) {
      return
    }

    const fetchImages = async () => {
      const results = await Promise.all(
        attachments.map(async (file) => {
          if (!file.url) {
            return null
          }

          const isProtected =
            file.url.includes('incidentattachment') ||
            file.url.includes('attachmentToken')

          if (!isProtected) {
            return file.url
          }

          try {
            const response = await fetch(file.url, {
              method: 'GET',
              credentials: 'include',
            })

            if (!response.ok) {
              return null
            }

            const blob = await response.blob()
            return URL.createObjectURL(blob)
          } catch (err) {
            console.error('Error fetching blob:', err)
            return null
          }
        })
      )

      setBlobUrls(results)
    }

    fetchImages()
  }, [attachments])

  // Build lightbox slides
  const slides = useMemo(
    () =>
      blobUrls.map((src, idx) => ({
        src: src || '',
        alt: attachments[idx]?.name || 'Attachment',
      })),
    [blobUrls, attachments]
  )

  // Handle image load
  const handleImageLoaded = (i) => {
    setLoaded((prev) => {
      const next = [...prev]
      next[i] = true
      return next
    })
  }

  // Show loading skeletons when needed
  if (isLoading && attachmentsCount > 0) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
        {Array.from({ length: attachmentsCount }).map((_, i) => (
          <ImagePlaceholder key={i} />
        ))}
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
        }}>
        {attachments.length > 0 ? (
          attachments.map((file, i) => (
            <Box
              key={i}
              onClick={() => {
                if (blobUrls[i]) {
                  setIndex(i)
                  setOpen(true)
                }
              }}
              sx={{
                position: 'relative',
                width: 90,
                height: 90,
                margin: '0px 16px 16px 0px',
                cursor: loaded[i] ? 'zoom-in' : 'default',
              }}>
              {!loaded[i] && (
                <Skeleton
                  variant="rectangular"
                  width={90}
                  height={90}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    borderRadius: '4px',
                    zIndex: 2,
                  }}/>
              )}

              {blobUrls[i] && (
                <LazyLoadImage
                  crossOrigin="use-credentials"
                  decoding="async"
                  alt={file.name}
                  src={blobUrls[i]}
                  onLoad={() => handleImageLoaded(i)}
                  onError={(e) => {
                    handleImageLoaded(i)
                    e.target.src =
                      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAEklEQVR42mNk+M9QzwAEYBxVSFMAAAD+AAHsCwoNAAAAAElFTkSuQmCC'
                  }}
                  style={{
                    width: 90,
                    height: 90,
                    objectFit: 'cover',
                    borderRadius: '4px',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                  }}/>
              )}
            </Box>
          ))
        ) : (
          <Box sx={{ color: 'text.secondary', fontSize: 14 }}>
            No attachments
          </Box>
        )}
      </Box>

      {blobUrls.length > 0 && (
        <Lightbox
          open={open}
          close={() => setOpen(false)}
          slides={slides}
          index={index}
          render={{ buttonPrev: undefined, buttonNext: undefined }}/>
      )}
    </>
  )
}

LightboxGallery.propTypes = {
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string.isRequired,
      mimeType: PropTypes.string,
      latLon: PropTypes.any,
    })
  ),
  attachmentsCount: PropTypes.number,
  isLoading: PropTypes.bool,
}

export default LightboxGallery
